import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HotelIcon from '@mui/icons-material/Hotel';
import { Box, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `64px`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  flexShrink: 0,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const navItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/client/dashboard' },
  { text: 'My Stays', icon: <HotelIcon />, path: '/my-stays' },
  { text: 'Payment History', icon: <PaymentsOutlinedIcon />, path: '/client/payment-history' },
  // { text: 'Check-In', icon: <CheckCircleIcon />, path: '/check-in' },
  // { text: 'Check-Out', icon: <ExitToAppIcon />, path: '/check-out' },
  // { text: 'Booking Cancellation', icon: <CancelIcon />, path: '/booking-cancellation' },
];

const SideDrawer = ({ open }: { open: boolean }) => {
  const theme = useTheme();
  const location = useLocation();
  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        {open && (
          <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
             <img src="/logo.png" alt="BellHop Logo" width={100} />
          </Box>
        )}
      </DrawerHeader>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)', // Subtract the header height
          overflow: 'hidden',
        }}
      >
        <List 
          sx={{ 
            mt: 1,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem
                key={item.text}
                component={Link}
                to={item.path}
                sx={{
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 2 : 1,
                  my: 0.5,
                  borderRadius: 2,
                  background: isActive ? '#3456D5' : 'transparent',
                  color: isActive ? '#fff' : '#b0b8c1',
                  '&:hover': {
                    background: isActive ? '#3456D5' : 'rgba(52,86,213,0.08)',
                    color: '#fff',
                  },
                  minHeight: 48,
                  transition: 'background 0.2s',
                  borderLeft: isActive ? '6px solid #3456D5' : '6px solid transparent',
                }}
              >
                <ListItemIcon sx={{ color: isActive ? '#fff' : '#b0b8c1', minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

export default SideDrawer; 